import React, { ReactNode, useState, memo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { RootState } from "../store";
import { logout, setUser } from "../store/slices/authSlice";

import {
  useGetBalanceQuery,
  useCreateWithdrawalMutation,
  useGetExchangeRateQuery,
  useToggleOnlineStatusMutation,
  useGetProfileQuery,
} from "../store/api";
import { useExchangeRateSocket } from "../hooks/useExchangeRateSocket";
import ThemeToggle from "./ThemeToggle";
import { useTheme } from "../hooks/useTheme";

const LayoutContainer = styled.div`
  display: flex;
  min-height: 100vh;
  background: var(--gradient-bg);
  position: relative;
`;

const Sidebar = styled.aside<{ isOpen: boolean; isMobile: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  width: 280px;
  background: transparent;
  color: var(--text-inverse);
  overflow: visible;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 200;
  transform: translateX(
    ${(props) => (props.isMobile ? (props.isOpen ? "0" : "-100%") : "0")}
  );
  pointer-events: ${(props) =>
    props.isMobile ? (props.isOpen ? "auto" : "none") : "auto"};

  @media (max-width: 768px) {
    background: var(--bg-sidebar-mobile);
    backdrop-filter: blur(12px);
  }
`;

const SidebarContent = styled.div`
  width: 280px;
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 0;
`;

const Navigation = styled.nav`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 0 2rem 2rem 2rem;
  flex: 1;
  justify-content: center;
`;

const NavItem = styled(Link)<{ isActive: boolean }>`
  display: flex;
  align-items: center;
  padding: 1rem 1.5rem;
  color: ${(props) => (props.isActive ? "var(--nav-active-text)" : "var(--nav-text)")};
  background: ${(props) =>
    props.isActive ? "var(--nav-active-bg)" : "var(--nav-bg)"};
  backdrop-filter: blur(12px);
  border-radius: 1rem;
  text-decoration: none;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  gap: 1rem;
  font-weight: ${(props) => (props.isActive ? "700" : "600")};
  font-size: 0.875rem;
  border: 1px solid
    ${(props) =>
      props.isActive ? "var(--nav-active-border)" : "var(--nav-border)"};
  box-shadow: var(--shadow-card);

  &:hover {
    background: ${(props) =>
      props.isActive ? "var(--nav-active-bg)" : "var(--bg-glass)"};
    transform: translateY(-2px);
    box-shadow: var(--shadow-md);
  }
`;

const NavIcon = styled.div<{ isActive?: boolean }>`
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 0.75rem;
  background: ${(props) =>
    props.isActive ? "var(--bg-card)" : "var(--bg-hover)"};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  font-weight: 700;
  color: ${(props) => (props.isActive ? "var(--accent-primary)" : "var(--text-primary)")};
  flex-shrink: 0;
  transition: all 0.3s ease;

  ${NavItem}:hover & {
    background: ${(props) =>
      props.isActive ? "var(--bg-card)" : "var(--bg-card-hover)"};
    transform: scale(1.05);
  }
`;

const MainContent = styled.main<{ sidebarOpen: boolean; isMobile: boolean }>`
  width: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 100vh;
  margin-left: ${(props) =>
    !props.isMobile && props.sidebarOpen ? "280px" : "0"};
  transition: margin-left 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  @media (max-width: 768px) {
    margin-left: 0;
  }
`;

const Header = styled.header`
  background: transparent;
  padding: 0 2rem;
  height: 72px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: sticky;
  top: 0;
  z-index: 100;
  width: 100%;

  @media (max-width: 768px) {
    padding: 0 1rem;
    height: 64px;
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem 2rem 1rem 2rem;
  margin-bottom: 1rem;
`;

const Logo = styled.img`
  height: 28px;
  width: auto;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    transform: scale(1.05);
  }

  @media (max-width: 768px) {
    height: 24px;
  }
`;

const HeaderTitle = styled.h1`
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--text-primary);
  letter-spacing: -0.025em;

  @media (max-width: 768px) {
    font-size: 1.25rem;
  }
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;

  @media (max-width: 768px) {
    gap: 1rem;
  }
`;

const BalanceSection = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem 1.5rem;
  background: var(--bg-glass);
  border-radius: 1rem;
  border: 1px solid var(--border-light);
  backdrop-filter: blur(12px);

  @media (max-width: 768px) {
    padding: 0.5rem 1rem;
    gap: 0.5rem;
  }
`;

const ExchangeRateSection = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.5rem 1rem;
  background: var(--bg-glass);
  border-radius: 0.75rem;
  border: 1px solid var(--border-color);
  backdrop-filter: blur(12px);
  height: 56px;

  @media (max-width: 768px) {
    padding: 0.375rem 0.75rem;
    gap: 0.5rem;
    height: 48px;
  }
`;

const ExchangeRateDisplay = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const ExchangeRateAmount = styled.div`
  font-size: 1.125rem;
  font-weight: 800;
  color: var(--text-primary);
  line-height: 1.2;
`;

const ExchangeRateLabel = styled.div`
  font-size: 0.6875rem;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: 600;
`;

const BalanceDisplay = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const BalanceAmount = styled.div`
  font-size: 1.125rem;
  font-weight: 800;
  color: var(--text-primary);
  line-height: 1.2;
`;

const BalanceLabel = styled.div`
  font-size: 0.6875rem;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: 600;
`;

const WithdrawButton = styled.button`
  background: var(--gradient-withdraw);
  color: #ffffff;
  border: none;
  border-radius: 0.5rem;
  padding: 0.5rem 1rem;
  font-size: 0.75rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;

  &:hover {
    background: var(--gradient-withdraw-hover);
    transform: translateY(-1px);
    box-shadow: var(--shadow-withdraw);
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 768px) {
    padding: 0.375rem 0.75rem;
    font-size: 0.6875rem;
  }
`;

const UserMenu = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem 1rem;
  background: var(--bg-glass);
  border-radius: 0.75rem;
  border: 1px solid var(--border-color);
  transition: all 0.3s ease;
  height: 56px;

  &:hover {
    background: var(--bg-card);
    box-shadow: var(--shadow-card);
  }

  @media (max-width: 768px) {
    padding: 0.375rem 0.75rem;
    height: 48px;
  }
`;

const UserAvatar = styled.div`
  width: 2.5rem;
  height: 2.5rem;
  background: var(--gradient-avatar);
  border-radius: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-weight: 700;
  font-size: 1rem;
  box-shadow: var(--shadow-avatar);

  @media (max-width: 768px) {
    width: 2rem;
    height: 2rem;
    font-size: 0.875rem;
  }
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  @media (max-width: 768px) {
    display: none;
  }
`;

const UserName = styled.span`
  font-size: 1rem;
  font-weight: 700;
  color: var(--text-primary);
  line-height: 1.2;
`;

const UserRole = styled.span`
  font-size: 0.75rem;
  color: var(--text-secondary);
  line-height: 1.2;
`;

const OnlineStatusToggle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: var(--bg-glass);
  border-radius: 0.75rem;
  border: 1px solid var(--border-color);
  transition: all 0.3s ease;
  cursor: pointer;
  user-select: none;
  box-shadow: var(--shadow-sm);
  width: 150px;
  height: 56px;

  &:hover {
    background: var(--bg-card);
    box-shadow: var(--shadow-md);
    border-color: var(--accent-primary);
  }

  &:active {
    transform: scale(0.98);
  }

  @media (max-width: 768px) {
    padding: 0.375rem 0.75rem;
    gap: 0.375rem;
    width: 130px;
    height: 48px;
  }
`;

const StatusIndicator = styled.div<{ isActive: boolean }>`
  width: 2rem;
  height: 1rem;
  border-radius: 0.5rem;
  background: ${(props) => (props.isActive ? "var(--status-active)" : "var(--status-inactive)")};
  position: relative;
  transition: all 0.3s ease;

  &::after {
    content: "";
    position: absolute;
    top: 0.125rem;
    left: ${(props) => (props.isActive ? "1.125rem" : "0.125rem")};
    width: 0.75rem;
    height: 0.75rem;
    background: #ffffff;
    border-radius: 50%;
    transition: all 0.3s ease;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  }
`;

const StatusText = styled.span<{ isActive: boolean }>`
  font-size: 0.8rem;
  font-weight: 600;
  color: ${(props) => (props.isActive ? "var(--status-active)" : "var(--text-secondary)")};
  transition: all 0.3s ease;
  white-space: nowrap;
  flex: 1;
  text-align: center;

  @media (max-width: 768px) {
    font-size: 0.75rem;
  }
`;

const LogoutButton = styled.button`
  background: var(--bg-glass);
  border: 1px solid var(--border-color);
  color: var(--text-secondary);
  cursor: pointer;
  padding: 0.5rem 1rem;
  border-radius: 0.75rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  font-size: 0.875rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 56px;

  &:hover {
    background: var(--accent-error-bg);
    color: var(--accent-error);
    border-color: var(--accent-error-border);
    transform: translateY(-1px);
  }

  @media (max-width: 768px) {
    padding: 0.375rem 0.75rem;
    font-size: 0.75rem;
    height: 48px;
  }
`;

const ContentArea = styled.div`
  flex: 1;
  overflow-y: auto;
  background: var(--gradient-bg);
`;

const Overlay = styled.div<{ isVisible: boolean }>`
  display: ${(props) => (props.isVisible ? "block" : "none")};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--bg-modal-overlay);
  backdrop-filter: blur(4px);
  z-index: 150;
  transition: all 0.3s ease;

  @media (min-width: 769px) {
    display: none;
  }
`;

// Модальное окно вывода средств
const WithdrawModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--bg-modal-overlay);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const WithdrawModalContent = styled.div`
  background: var(--bg-card);
  border-radius: 0.75rem;
  padding: 2rem;
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
`;

const WithdrawModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const WithdrawModalTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
`;

const WithdrawCloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: var(--text-muted);

  &:hover {
    color: var(--text-primary);
  }
`;

const WithdrawFormGroup = styled.div`
  margin-bottom: 1rem;
`;

const WithdrawLabel = styled.label`
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-primary);
  margin-bottom: 0.5rem;
`;

const WithdrawInput = styled.input`
  width: 100%;
  padding: 0.75rem;
  background: var(--bg-input);
  border: 1px solid var(--border-input);
  border-radius: 0.5rem;
  font-size: 0.875rem;
  color: var(--text-primary);

  &:focus {
    outline: none;
    border-color: var(--accent-primary);
    box-shadow: var(--focus-ring);
  }

  &:invalid {
    border-color: var(--accent-error);
  }
`;

const WithdrawButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 1.5rem;
`;

const WithdrawFormButton = styled.button<{ variant?: "primary" | "secondary" }>`
  padding: 0.75rem 1.5rem;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  ${({ variant }) =>
    variant === "primary"
      ? `
    background: var(--accent-primary);
    color: #ffffff;
    border: none;

    &:hover {
      background: var(--accent-primary-hover);
    }
  `
      : `
    background: var(--bg-card);
    color: var(--text-primary);
    border: 1px solid var(--border-input);

    &:hover {
      background: var(--bg-primary);
    }
  `}

  &:disabled {
    background: var(--bg-input-disabled);
    color: var(--text-disabled);
    cursor: not-allowed;
  }
`;

const WithdrawErrorText = styled.div`
  color: var(--accent-error);
  font-size: 0.875rem;
  margin-top: 0.5rem;
`;

const WithdrawSuccessText = styled.div`
  color: var(--accent-success);
  font-size: 0.875rem;
  margin-top: 0.5rem;
`;

const WalletInfoCard = styled.div<{ isActive: boolean }>`
  background: ${(props) => (props.isActive ? "var(--bg-deposit-info)" : "var(--bg-card-hover)")};
  border: 2px solid ${(props) => (props.isActive ? "var(--border-deposit-info)" : "var(--border-color)")};
  border-radius: 0.75rem;
  padding: 1.5rem;
  margin-top: 1rem;
`;

const WalletAddress = styled.div`
  font-family: monospace;
  font-size: 0.875rem;
  background: var(--bg-code-inline);
  color: var(--text-primary);
  padding: 0.75rem;
  border-radius: 0.375rem;
  margin: 0.5rem 0;
  word-break: break-all;
  cursor: pointer;

  &:hover {
    background: var(--bg-code-inline-hover);
  }
`;

const TimeLeft = styled.div<{ isUrgent: boolean }>`
  font-size: 1rem;
  font-weight: 600;
  color: ${(props) => (props.isUrgent ? "var(--status-urgent)" : "var(--text-secondary)")};
  margin-top: 0.5rem;
`;

const Alert = styled.div<{ type: "info" | "warning" | "error" | "success" }>`
  padding: 1rem;
  border-radius: 0.5rem;
  margin-bottom: 1rem;
  background: ${(props) => `var(--alert-${props.type}-bg)`};
  border: 1px solid ${(props) => `var(--alert-${props.type}-border)`};
  color: ${(props) => `var(--alert-${props.type}-text)`};
`;

// Мемоизированный компонент для курса валют, чтобы избежать дерганья при re-render
const ExchangeRateComponent = memo(
  ({ exchangeRate }: { exchangeRate: any }) => (
    <ExchangeRateSection>
      <ExchangeRateDisplay>
        <ExchangeRateLabel>Курс USDT/RUB</ExchangeRateLabel>
        <ExchangeRateAmount>
          {exchangeRate ? `${exchangeRate.rate.toFixed(2)} ₽` : "—"}
        </ExchangeRateAmount>
      </ExchangeRateDisplay>
    </ExchangeRateSection>
  )
);

interface LayoutProps {
  children: ReactNode;
}

const navItems = [
  {
    path: "/dashboard",
    label: "Дашборд",
    icon: "D",
    description: "Главная панель",
  },
  {
    path: "/orders",
    label: "Ордера",
    icon: "O",
    description: "Управление заказами",
  },
  {
    path: "/balance",
    label: "Баланс",
    icon: "B",
    description: "Управление балансом",
  },
  {
    path: "/appeals",
    label: "Апелляции",
    icon: "A",
    description: "Споры и жалобы",
  },
  {
    path: "/settings",
    label: "Настройки",
    icon: "S",
    description: "Конфигурация системы",
  },
];

function Layout({ children }: LayoutProps) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { isDark } = useTheme();

  const { user } = useSelector((state: RootState) => state.auth);
  const { data: balance } = useGetBalanceQuery();
  // Получаем первоначальный курс через REST API
  const { data: initialExchangeRate } = useGetExchangeRateQuery();

  // Используем WebSocket для получения обновлений курса в реальном времени
  const {
    exchangeRate: wsExchangeRate,
    isConnected: wsConnected,
    error: wsError,
  } = useExchangeRateSocket();

  // Используем курс из WebSocket, если доступен, иначе из REST API
  const exchangeRate = wsExchangeRate || initialExchangeRate;

  // Логирование для отладки
  React.useEffect(() => {
    console.log("WebSocket connected:", wsConnected);
    console.log("WebSocket error:", wsError);
    console.log("Exchange rate (WS):", wsExchangeRate);
    console.log("Exchange rate (REST):", initialExchangeRate);
    console.log("Final exchange rate:", exchangeRate);
  }, [wsConnected, wsError, wsExchangeRate, initialExchangeRate, exchangeRate]);

  // Логирование изменений статуса пользователя
  React.useEffect(() => {
    console.log("🔍 Пользователь изменился:", {
      user: user,
      providerStatus: user?.provider?.status,
      isActive: user?.provider?.status === "ACTIVE",
    });
  }, [user]);

  const [toggleOnlineStatus] = useToggleOnlineStatusMutation();
  const { refetch: refetchProfile } = useGetProfileQuery();

  const [isMobile, setIsMobile] = React.useState(window.innerWidth <= 768);
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [depositStep, setDepositStep] = useState(1); // 1 - ввод суммы, 2 - резервирование кошелька
  const [depositForm, setDepositForm] = useState({
    amount: "",
    method: "crypto", // crypto, sbp, bank_transfer
    cardNumber: "",
    phoneNumber: "",
  });
  const [reservedWallet, setReservedWallet] = useState<any>(null);
  const [timeLeft, setTimeLeft] = useState(0);
  const [isRequestingWallet, setIsRequestingWallet] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  React.useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
    };

    // Инициализация
    handleResize();

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Проверяем наличие зарезервированного кошелька при загрузке
  React.useEffect(() => {
    checkReservedWallet();
  }, []);

  // Таймер для обновления времени резервирования кошелька
  React.useEffect(() => {
    if (!reservedWallet) return;

    const timer = setInterval(() => {
      const now = new Date().getTime();
      const reserved = new Date(reservedWallet.reservedUntil).getTime();
      const diff = Math.max(0, Math.floor((reserved - now) / 1000));

      setTimeLeft(diff);

      if (diff <= 0) {
        setReservedWallet(null);
        setDepositStep(1);
        clearInterval(timer);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [reservedWallet]);

  const handleLogout = () => {
    dispatch(logout());
    navigate("/login");
  };

  const handleToggleOnlineStatus = async () => {
    try {
      console.log("🔄 Переключение статуса активности...");
      console.log("🔍 Текущий статус до изменения:", user?.provider?.status);

      const result = await toggleOnlineStatus().unwrap();
      console.log("✅ Статус успешно изменен:", result);

      // Принудительно обновляем профиль с сервера для гарантии актуальности
      try {
        const profileData = await refetchProfile().unwrap();
        console.log("🔄 Профиль обновлен с сервера:", profileData);
        dispatch(setUser(profileData));
        console.log("✅ UI должен обновиться с новыми данными");
      } catch (profileError) {
        console.error("❌ Ошибка при обновлении профиля:", profileError);

        // Fallback: обновляем локально если refetch не сработал
        if (user && user.provider) {
          const updatedUser = {
            ...user,
            provider: {
              ...user.provider,
              status: result.status,
            },
          };
          console.log("🔍 Fallback: обновленный пользователь:", updatedUser);
          dispatch(setUser(updatedUser));
        }
      }
    } catch (error) {
      console.error("❌ Ошибка при переключении статуса:", error);
    }
  };

  const getUserInitials = () => {
    if (!user?.login) return "P";
    return user.login.charAt(0).toUpperCase();
  };

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs.toString().padStart(2, "0")}`;
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setSuccess("Адрес скопирован в буфер обмена");
    setTimeout(() => setSuccess(""), 2000);
  };

  const checkReservedWallet = async () => {
    try {
      const response = await fetch("/api/wallets/my-reserved", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data && data.address) {
          setReservedWallet(data);
          setTimeLeft(data.timeLeft);
          setDepositStep(2);
          // Восстанавливаем сумму из данных кошелька если она есть
          if (data.reservedAmount) {
            setDepositForm((prev) => ({
              ...prev,
              amount: data.reservedAmount.toString(),
            }));
          }
        }
      }
    } catch (error) {
      console.error("Ошибка при проверке кошелька:", error);
    }
  };

  const requestWallet = async () => {
    setIsRequestingWallet(true);
    setError("");

    try {
      const response = await fetch("/api/wallets/reserve", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          amount: parseFloat(depositForm.amount),
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setReservedWallet(data);
        setTimeLeft(data.timeLeft || 1800); // 30 минут по умолчанию
        setDepositStep(2);
      } else {
        const error = await response.json();
        setError(error.message || "Ошибка при запросе кошелька");
      }
    } catch (error) {
      console.error("Ошибка при запросе кошелька:", error);
      setError("Ошибка при запросе кошелька");
    } finally {
      setIsRequestingWallet(false);
    }
  };

  const releaseWallet = async () => {
    if (!reservedWallet) return;

    try {
      const response = await fetch(
        `/api/wallets/release/${reservedWallet.walletId}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.ok) {
        setReservedWallet(null);
        setTimeLeft(0);
        setDepositStep(1);
        // Сбрасываем форму при ручном освобождении кошелька
        setDepositForm({
          amount: "",
          method: "crypto",
          cardNumber: "",
          phoneNumber: "",
        });
      }
    } catch (error) {
      console.error("Ошибка при освобождении кошелька:", error);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setDepositForm({ ...depositForm, [name]: value });
    setError("");
    setSuccess("");
  };

  const handleDepositSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (depositStep === 1) {
      // Первый шаг - валидация суммы
      if (!depositForm.amount) {
        setError("Пожалуйста, введите сумму");
        return;
      }

      const amount = parseFloat(depositForm.amount);
      if (isNaN(amount) || amount <= 0) {
        setError("Пожалуйста, введите корректную сумму");
        return;
      }

      if (amount < 1) {
        setError("Минимальная сумма пополнения: 1 USDT");
        return;
      }

      // Если выбран крипто метод, переходим к резервированию кошелька
      if (depositForm.method === "crypto") {
        await requestWallet();
      } else {
        // Для других методов - проверяем дополнительные поля
        if (depositForm.method === "sbp" && !depositForm.phoneNumber) {
          setError("Введите номер телефона для СБП");
          return;
        }

        if (depositForm.method === "bank_transfer" && !depositForm.cardNumber) {
          setError("Введите номер карты");
          return;
        }

        try {
          // Здесь будет API для создания заявки на пополнение
          setSuccess("Заявка на пополнение создана успешно!");
          setTimeout(() => {
            handleCloseDepositModal();
          }, 2000);
        } catch (err: any) {
          setError(
            err?.data?.message || "Произошла ошибка при создании заявки"
          );
        }
      }
    }
  };

  const handleCloseDepositModal = () => {
    setShowDepositModal(false);
    // НЕ сбрасываем состояние кошелька и шага, если кошелек зарезервирован
    if (!reservedWallet) {
      setDepositStep(1);
      setDepositForm({
        amount: "",
        method: "crypto",
        cardNumber: "",
        phoneNumber: "",
      });
    }
    setError("");
    setSuccess("");
  };

  return (
    <LayoutContainer>
      <Sidebar isOpen={!isMobile} isMobile={isMobile}>
        <SidebarContent>
          <LogoContainer>
            <Logo
              src={isDark ? "/VectorDark.svg" : "/Vector.svg"}
              alt="VangardPay Logo"
              onClick={() => navigate("/dashboard")}
            />
          </LogoContainer>
          <Navigation>
            {navItems.map((item) => (
              <NavItem
                key={item.path}
                to={item.path}
                isActive={location.pathname === item.path}
              >
                <NavIcon isActive={location.pathname === item.path}>
                  {item.icon}
                </NavIcon>
                <div>
                  <div>{item.label}</div>
                  <div
                    style={{
                      fontSize: "0.6875rem",
                      opacity: 0.7,
                      marginTop: "0.125rem",
                    }}
                  >
                    {item.description}
                  </div>
                </div>
              </NavItem>
            ))}
          </Navigation>
        </SidebarContent>
      </Sidebar>

      <MainContent sidebarOpen={!isMobile} isMobile={isMobile}>
        <Header>
          <HeaderLeft>{/* Заголовки страниц убраны */}</HeaderLeft>

          <HeaderRight>
            <ExchangeRateComponent exchangeRate={exchangeRate} />

            <OnlineStatusToggle onClick={handleToggleOnlineStatus}>
              <StatusIndicator isActive={user?.provider?.status === "ACTIVE"} />
              <StatusText isActive={user?.provider?.status === "ACTIVE"}>
                {user?.provider?.status === "ACTIVE" ? "Активен" : "Неактивен"}
              </StatusText>
            </OnlineStatusToggle>

            <UserMenu>
              <UserAvatar>{getUserInitials()}</UserAvatar>
              <UserInfo>
                <UserName>{user?.login}</UserName>
                <UserRole>Провайдер</UserRole>
              </UserInfo>
            </UserMenu>

            <ThemeToggle />

            <LogoutButton onClick={handleLogout}>Выйти</LogoutButton>
          </HeaderRight>
        </Header>

        <ContentArea>{children}</ContentArea>
      </MainContent>

      {/* Модальное окно пополнения баланса */}
      {showDepositModal && (
        <WithdrawModalOverlay>
          <WithdrawModalContent>
            <WithdrawModalHeader>
              <WithdrawModalTitle>Пополнение баланса</WithdrawModalTitle>
              <WithdrawCloseButton onClick={handleCloseDepositModal}>
                ×
              </WithdrawCloseButton>
            </WithdrawModalHeader>

            {depositStep === 1 ? (
              <form onSubmit={handleDepositSubmit}>
                <WithdrawFormGroup>
                  <WithdrawLabel htmlFor="amount">
                    Сумма пополнения (USDT)
                  </WithdrawLabel>
                  <WithdrawInput
                    type="number"
                    id="amount"
                    name="amount"
                    step="0.01"
                    min="1"
                    value={depositForm.amount}
                    onChange={handleInputChange}
                    placeholder="Введите сумму"
                  />
                </WithdrawFormGroup>

                <WithdrawFormGroup>
                  <WithdrawLabel htmlFor="method">
                    Способ пополнения
                  </WithdrawLabel>
                  <select
                    id="method"
                    name="method"
                    value={depositForm.method}
                    onChange={handleInputChange}
                    style={{
                      width: "100%",
                      padding: "0.75rem",
                      background: "var(--bg-input)",
                      border: "1px solid var(--border-input)",
                      borderRadius: "0.5rem",
                      fontSize: "0.875rem",
                      color: "var(--text-primary)",
                    }}
                  >
                    <option value="crypto">Криптовалюта (USDT TRC-20)</option>
                  </select>
                </WithdrawFormGroup>

                {error && <WithdrawErrorText>{error}</WithdrawErrorText>}
                {success && (
                  <WithdrawSuccessText>{success}</WithdrawSuccessText>
                )}

                <WithdrawButtonGroup>
                  <WithdrawFormButton
                    type="button"
                    variant="secondary"
                    onClick={handleCloseDepositModal}
                  >
                    Отмена
                  </WithdrawFormButton>
                  <WithdrawFormButton
                    type="submit"
                    variant="primary"
                    disabled={isRequestingWallet}
                  >
                    {isRequestingWallet ? "Запрос кошелька..." : "Далее"}
                  </WithdrawFormButton>
                </WithdrawButtonGroup>
              </form>
            ) : (
              // Шаг 2 - Отображение зарезервированного кошелька
              <div>
                {reservedWallet ? (
                  <WalletInfoCard isActive={true}>
                    <Alert type="info">
                      <strong>
                        Кошелек зарезервирован за вами на 30 минут
                      </strong>
                      <br />
                      Переведите {depositForm.amount} USDT TRC20 на указанный
                      адрес для пополнения баланса
                    </Alert>

                    <WithdrawLabel>Адрес для пополнения:</WithdrawLabel>
                    <WalletAddress
                      onClick={() => copyToClipboard(reservedWallet.address)}
                      title="Нажмите чтобы скопировать"
                    >
                      {reservedWallet.address}
                    </WalletAddress>

                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginTop: "1rem",
                      }}
                    >
                      <div>
                        <strong>Сеть:</strong>{" "}
                        {reservedWallet.network || "TRC-20"}
                        <br />
                        <strong>Сумма:</strong> {depositForm.amount} USDT
                      </div>
                      <TimeLeft isUrgent={timeLeft < 300}>
                        Осталось: {formatTime(timeLeft)}
                      </TimeLeft>
                    </div>

                    {error && <WithdrawErrorText>{error}</WithdrawErrorText>}
                    {success && (
                      <WithdrawSuccessText>{success}</WithdrawSuccessText>
                    )}

                    <WithdrawButtonGroup>
                      <WithdrawFormButton
                        type="button"
                        variant="secondary"
                        onClick={releaseWallet}
                      >
                        Освободить кошелек
                      </WithdrawFormButton>
                      <WithdrawFormButton
                        type="button"
                        variant="primary"
                        onClick={() => copyToClipboard(reservedWallet.address)}
                      >
                        Скопировать адрес
                      </WithdrawFormButton>
                    </WithdrawButtonGroup>
                  </WalletInfoCard>
                ) : (
                  <Alert type="error">
                    Не удалось зарезервировать кошелек. Попробуйте снова.
                  </Alert>
                )}
              </div>
            )}
          </WithdrawModalContent>
        </WithdrawModalOverlay>
      )}
    </LayoutContainer>
  );
}

export default Layout;
